import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Outlet, RouterProvider, createRootRoute, createRoute, createRouter, lazyRouteComponent, redirect } from "@tanstack/react-router";
import "@jaks/ui/tokens.css";
import "./shell/shell.css";
import { Shell } from "./shell/shell.tsx";
import { DETAIL_MODULES, LEGACY_REDIRECTS, MODULES, detailRouteOf, legacyRedirect } from "./manifest.ts";

const qc = new QueryClient({ defaultOptions: { queries: { refetchOnWindowFocus: true, retry: 1 } } });

const rootRoute = createRootRoute({ component: Outlet });
// The shell wraps every module screen.
const shellRoute = createRoute({ getParentRoute: () => rootRoute, id: "shell", component: Shell });
const indexRoute = createRoute({ getParentRoute: () => shellRoute, path: "/", beforeLoad: () => { throw redirect({ to: "/home" }); } });
const moduleRoutes = MODULES.map((m) =>
  createRoute({ getParentRoute: () => shellRoute, path: `/${m.id}`, component: m.component }),
);
// A record screen under a module: `/vendors/<id>`, `/sales/c/<accountId>`. A
// dynamic segment ranks above the splat, so an unknown deeper path still falls
// to not-found.
const detailRoutes = DETAIL_MODULES.map((m) =>
  createRoute({ getParentRoute: () => shellRoute, path: detailRouteOf(m), component: m.detail }),
);
// An address that used to be a section (`/customers`, T43) answers with where
// it went. A concrete route, so the splat below never sees it; `beforeLoad`
// rather than a component, so nothing renders and the history entry is the
// destination.
const redirectRoutes = LEGACY_REDIRECTS.map((r) =>
  createRoute({
    getParentRoute: () => shellRoute,
    path: r.from,
    beforeLoad: ({ location }) => {
      throw redirect({ href: legacyRedirect(r.from, location.searchStr) ?? "/home", replace: true });
    },
  }),
);
const moreRoute = createRoute({ getParentRoute: () => shellRoute, path: "/more", component: lazyRouteComponent(() => import("./modules/more.tsx")) });
// A splat under the shell, not the router's notFound: a not-found bubbles past
// a pathless layout to the root, which would drop the sidebar. Splat ranks
// below every concrete route, so it only catches what nothing else claimed.
const notFoundRoute = createRoute({ getParentRoute: () => shellRoute, path: "$", component: lazyRouteComponent(() => import("./shell/not-found.tsx")) });
const router = createRouter({ routeTree: rootRoute.addChildren([shellRoute.addChildren([indexRoute, moreRoute, ...moduleRoutes, ...detailRoutes, ...redirectRoutes, notFoundRoute])]) });
declare module "@tanstack/react-router" { interface Register { router: typeof router } }

// Installable app: the service worker precaches the shell; updates prompt
// rather than surprise-reload (a crew member mid-form keeps their screen).
import { registerSW } from "virtual:pwa-register";
const updateSW = registerSW({
  onNeedRefresh() {
    const bar = document.createElement("div");
    bar.setAttribute("role", "status");
    bar.style.cssText = "position:fixed;bottom:14px;left:50%;transform:translateX(-50%);z-index:80;background:var(--sidebar);color:#fff;padding:10px 16px;border-radius:12px;font:600 13px var(--font-sans);display:flex;gap:12px;align-items:center;box-shadow:0 8px 30px rgba(10,19,39,.35)";
    bar.innerHTML = "<span>A new version of JAKS is ready.</span>";
    const btn = document.createElement("button");
    btn.textContent = "Update now";
    btn.style.cssText = "background:var(--red-500);color:#fff;padding:6px 12px;border-radius:8px;font-weight:700";
    btn.onclick = () => void updateSW(true);
    bar.appendChild(btn);
    document.body.appendChild(bar);
  },
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={qc}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
